import React from 'react';
import { PageLayout } from '../components/PageLayout';
import { AdBanner } from '../components/AdBanner';
import { useAutoReturn } from '../hooks/useAutoReturn';
import { useLanguage } from '../i18n/LanguageContext';
import { AUTO_RETURN_SECONDS, getStoreId } from '../utils/config';
import type { WaitingResult } from '../api/waiting';
import styles from './CompletePage.module.css';

interface CompletePageProps {
  result: WaitingResult;
  onReset: () => void;
  isDemo?: boolean;
  /** 로그인 세션으로 바인딩된 매장 ID (URL ?storeId 미사용 시 광고 배너용) */
  storeId?: string;
}

/**
 * 등록 완료 페이지
 * - 체크 아이콘, 대기번호, 앞 대기팀 수, 예상 소요 시간
 * - 카카오톡 안내, 자동 복귀 카운트다운
 * - 광고 배너
 */
export function CompletePage({ result, onReset, isDemo = false, storeId: storeIdProp }: CompletePageProps) {
  const { t } = useLanguage();
  const { remainingSeconds } = useAutoReturn({
    seconds: AUTO_RETURN_SECONDS,
    onComplete: onReset,
    enabled: !isDemo,
  });

  // 데모 모드는 광고 미표시. 그 외에는 로그인 세션 storeId(prop) 우선, 없으면 URL ?storeId 폴백
  let storeId: string | null = null;
  if (!isDemo) {
    if (storeIdProp) {
      storeId = storeIdProp;
    } else {
      try {
        storeId = getStoreId();
      } catch {
        // storeId 없으면 광고 미표시
      }
    }
  }

  const unitSuffix = t('complete_unit');

  return (
    <PageLayout
      footer={
        <p className={styles.countdown}>
          {t('complete_return', { seconds: remainingSeconds })}
        </p>
      }
    >
      <div className={styles.content}>
        <div className={styles.checkIcon} />
        {(result.visitCount ?? 0) > 0 && (
          <p className={styles.welcomeBack}>
            🎉 {result.visitCount! + 1}번째 방문, 다시 찾아주셔서 감사합니다!
          </p>
        )}
        <p className={styles.waitingNumber}>
          {t('complete_number')} {result.waitingNumber}{unitSuffix}
        </p>
        <p className={styles.aheadText}>
          {t('complete_ahead', { count: result.ahead })}
        </p>
        <p className={styles.estimateText}>
          {t('complete_time', { minutes: result.estimatedMinutes })}
        </p>
        <p className={styles.noticeText}>
          {t('complete_notice')}
        </p>
        {isDemo && (
          <div className={styles.demoNotice}>
            <strong>데모 모드</strong>
            <br />
            이 결과는 시뮬레이션입니다. 실제 대기 등록 및 알림톡 발송은 이루어지지 않았습니다.
            <br />
            <button type="button" className={styles.demoResetBtn} onClick={onReset}>
              처음부터 다시 체험하기
            </button>
          </div>
        )}
        {storeId && (
          <>
            <p className={styles.adSuggestion}>
              {t('ad_suggestion')}
            </p>
            <AdBanner storeId={storeId} />
          </>
        )}
      </div>
    </PageLayout>
  );
}
